import React, { createContext, useContext, useEffect, useState } from 'react'
import { useCustomFetch } from '../hooks/useCustomFetch'
import toast from 'react-hot-toast'
import { RoadmapContext } from './RoadmapContext'
import { FeedItem } from '@/types/feedItems'

interface FeedContextType {
  allFeedItems: Record<string, FeedItem[]>
  filteredFeedItems: FeedItem[]
  feedsLoading: boolean
  selectedTab: string
  setSelectedTab: (selectedTab: string) => void
  getFeedItems: (isNewProduct?: boolean, isInfiniteScroll?: boolean) => void
  hasMoreItems: boolean
}

export const FeedContext = createContext<FeedContextType>({} as FeedContextType)

export const FeedContextProvider = ({ children }) => {
  const customFetch = useCustomFetch()
  const { selectedProduct, products } = useContext(RoadmapContext)

  const [allFeedItems, setAllFeedItems] = useState<Record<string, FeedItem[]>>({})
  const [filteredFeedItems, setFilteredFeedItems] = useState<FeedItem[]>([])
  const [feedsLoading, setFeedsLoading] = useState(false) // Start as false - only show loading when actually fetching
  const [selectedTab, setSelectedTab] = useState('all')
  const [hasMoreItems, setHasMoreItems] = useState(true)
  const [lastVisible, setLastVisible] = useState<Record<string, any>>({}) // { [productId]: timestamp }

  const getFeedItems = async (isNewProduct = false, isInfiniteScroll = false) => {
    if (!selectedProduct.id) {
      // If no product is selected, don't show loading state
      setFeedsLoading(false)
      return
    }

    // Only set feedsLoading to true for initial loads, not for infinite scroll
    if (!isInfiniteScroll) {
      setFeedsLoading(true)
    }

    try {
      const res = await customFetch('/getFeedItems', 'POST', {
        selectedProduct,
        lastVisibleFeedItem: isNewProduct ? null : lastVisible[selectedProduct.id],
      })

      if (!Array.isArray(res.feedItems)) {
        toast.error('Error fetching feed items')
        setHasMoreItems(false)
        return
      }

      if (res.feedItems.length === 0) {
        setHasMoreItems(false)
      }

      if (res.lastVisible) {
        setLastVisible((prev) => ({ ...prev, [selectedProduct.id]: res.lastVisible }))
      }

      const newItems = res.feedItems as FeedItem[]
      const currentItems = isNewProduct ? [] : allFeedItems[selectedProduct.id] || []
      const updatedItems = [...currentItems, ...newItems]

      setAllFeedItems((prev) => ({ ...prev, [selectedProduct.id]: updatedItems }))
    } catch (err) {
      console.error('Error fetching feed items!', err)
      toast.error('Could not load feed.')
      setHasMoreItems(false)
    } finally {
      // Only set feedsLoading to false if we set it to true at the beginning
      if (!isInfiniteScroll) {
        setFeedsLoading(false)
      }
    }
  }

  // Effect to filter items whenever the source list or tab changes
  useEffect(() => {
    if (!selectedProduct.id) {
      // Clear filtered items if no product is selected, but don't show loading
      setFilteredFeedItems([])
      return
    }

    const itemsForProduct = allFeedItems[selectedProduct.id] || []
    if (selectedTab === 'all') {
      setFilteredFeedItems(itemsForProduct)
    } else {
      setFilteredFeedItems(itemsForProduct.filter((item) => item.type === selectedTab))
    }
  }, [allFeedItems, selectedProduct, selectedTab])

  // Effect to fetch items when product changes
  useEffect(() => {
    if (products.length > 0 && selectedProduct.id) {
      // If we haven't loaded data for this product before, fetch it.
      if (!allFeedItems[selectedProduct.id]) {
        setHasMoreItems(true)
        getFeedItems(true, false) // isNewProduct = true, isInfiniteScroll = false
      } else {
        // If we already have data for this product, make sure loading is false
        setFeedsLoading(false)
      }
    } else if (selectedProduct.id === undefined || selectedProduct.id === null) {
      // If no product is selected, ensure loading is false
      setFeedsLoading(false)
    }
  }, [products, selectedProduct])

  return (
    <FeedContext.Provider
      value={{
        allFeedItems,
        filteredFeedItems,
        feedsLoading,
        selectedTab,
        setSelectedTab,
        getFeedItems,
        hasMoreItems,
      }}
    >
      {children}
    </FeedContext.Provider>
  )
}
